import React, { useState, useEffect, useMemo } from 'react';
import { tasksApi, usersApi, projectsApi } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  ChevronLeft, ChevronRight, Calendar as CalIcon, Clock,
  RefreshCw, Repeat, CheckSquare
} from 'lucide-react';
import { toast } from 'sonner';
import TaskEditDialog from '../components/TaskEditDialog';

const statusColors = {
  todo: '#1A2B5E', in_progress: '#106EFD', review: '#E171EA', done: '#37C563'
};
const priorityStyle = (p) => p === 'high' ? 'bg-red-100 text-red-700' : p === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const CalendarPage = () => {
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // month | week
  const [editingTask, setEditingTask] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const fetchData = async () => {
    try {
      const start = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1).toISOString();
      const end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 2, 0).toISOString();
      const [tasksRes, usersRes, projRes] = await Promise.all([
        tasksApi.getCalendar(start, end),
        usersApi.getAll(),
        projectsApi.getAll({})
      ]);
      setTasks(tasksRes.data);
      setUsers(usersRes.data);
      setProjects(projRes.data);
    } catch { toast.error('Failed to load calendar'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [currentDate]);

  const handleClaimRecurring = async (task) => {
    try {
      const result = await tasksApi.claimRecurring(task.recurring_template_id, task.due_date);
      toast.success(`Task "${task.title}" claimed for ${task.due_date}`);
      fetchData();
    } catch { toast.error('Failed to claim task'); }
  };

  const handleTaskClick = (task) => {
    if (task.is_occurrence) {
      handleClaimRecurring(task);
    } else {
      setEditingTask(task);
      setEditDialogOpen(true);
    }
  };

  // Navigation
  const navigate = (dir) => {
    const d = new Date(currentDate);
    if (viewMode === 'month') {
      d.setMonth(d.getMonth() + dir);
    } else {
      d.setDate(d.getDate() + dir * 7);
    }
    setCurrentDate(d);
  };

  const goToday = () => setCurrentDate(new Date());

  // Month view grid
  const monthGrid = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const prevMonthDays = new Date(year, month, 0).getDate();

    const cells = [];
    // Previous month padding
    for (let i = firstDay - 1; i >= 0; i--) {
      cells.push({ date: new Date(year, month - 1, prevMonthDays - i), isCurrentMonth: false });
    }
    // Current month
    for (let i = 1; i <= daysInMonth; i++) {
      cells.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    // Next month padding
    const remaining = 42 - cells.length;
    for (let i = 1; i <= remaining; i++) {
      cells.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    return cells;
  }, [currentDate]);

  // Week view
  const weekDays = useMemo(() => {
    const d = new Date(currentDate);
    const day = d.getDay();
    const start = new Date(d);
    start.setDate(d.getDate() - day);
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      return date;
    });
  }, [currentDate]);

  // Get tasks for a specific date
  const getTasksForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return tasks.filter(t => {
      const due = t.due_date?.split('T')[0];
      const start = t.start_date?.split('T')[0];
      return due === dateStr || start === dateStr;
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
  };

  // Task pill component
  const TaskPill = ({ task }) => {
    const color = statusColors[task.status] || statusColors.todo;
    const assignee = users.find(u => u.id === task.assignee_id);
    return (
      <button
        onClick={(e) => { e.stopPropagation(); handleTaskClick(task); }}
        className={`w-full text-left px-2 py-1 rounded text-xs truncate transition-all hover:brightness-90 ${task.is_occurrence ? 'border border-dashed border-navy/30 bg-white' : ''}`}
        style={!task.is_occurrence ? { backgroundColor: `${color}15`, borderLeft: `3px solid ${color}` } : {}}
        title={`${task.title}${task.is_occurrence ? ' (Click to claim)' : ''}`}
        data-testid={`cal-task-${task.id}`}
      >
        <span className="flex items-center gap-1">
          {task.is_occurrence && <Repeat className="w-3 h-3 text-navy/40 flex-shrink-0" />}
          {task.recurrence && !task.is_occurrence && <Repeat className="w-3 h-3 flex-shrink-0" style={{ color }} />}
          <span className={`truncate ${task.is_occurrence ? 'text-navy/50 italic' : 'font-medium'}`} style={!task.is_occurrence ? { color } : {}}>
            {task.title}
          </span>
        </span>
      </button>
    );
  };

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="w-10 h-10 loading-spinner" /></div>;

  return (
    <div className="space-y-4" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1A2B5E] font-['Amaranth']">Calendar</h1>
          <p className="text-navy/60 mt-0.5">View tasks and recurring schedules</p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => navigate(-1)} data-testid="cal-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToday} data-testid="cal-today">Today</Button>
          <Button variant="outline" size="icon" onClick={() => navigate(1)} data-testid="cal-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <h2 className="text-xl font-bold text-[#1A2B5E] font-['Amaranth'] ml-3">
            {viewMode === 'month'
              ? `${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`
              : `Week of ${weekDays[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
            }
          </h2>
        </div>
        <div className="flex bg-navy/5 rounded-lg p-0.5" data-testid="cal-view-toggle">
          <button
            onClick={() => setViewMode('month')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${viewMode === 'month' ? 'bg-white shadow-sm text-[#1A2B5E]' : 'text-navy/50'}`}
            data-testid="cal-month-btn"
          >
            Month
          </button>
          <button
            onClick={() => setViewMode('week')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${viewMode === 'week' ? 'bg-white shadow-sm text-[#1A2B5E]' : 'text-navy/50'}`}
            data-testid="cal-week-btn"
          >
            Week
          </button>
        </div>
      </div>

      {/* Month View */}
      {viewMode === 'month' && (
        <div className="bg-white rounded-xl border border-navy/10 overflow-hidden" data-testid="cal-month-view">
          {/* Day headers */}
          <div className="grid grid-cols-7 border-b border-navy/10">
            {DAYS.map(day => (
              <div key={day} className="px-2 py-2 text-center text-xs font-semibold text-navy/50 bg-gray-50">
                {day}
              </div>
            ))}
          </div>
          {/* Date cells */}
          <div className="grid grid-cols-7">
            {monthGrid.map((cell, i) => {
              const dayTasks = getTasksForDate(cell.date);
              return (
                <div
                  key={i}
                  className={`min-h-[100px] border-b border-r border-navy/5 p-1 ${!cell.isCurrentMonth ? 'bg-gray-50/50' : ''} ${isToday(cell.date) ? 'bg-[#40CFDE]/5' : ''}`}
                >
                  <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${isToday(cell.date) ? 'bg-[#40CFDE] text-white' : cell.isCurrentMonth ? 'text-navy/70' : 'text-navy/30'}`}>
                    {cell.date.getDate()}
                  </span>
                  <div className="mt-1 space-y-0.5">
                    {dayTasks.slice(0, 3).map(task => (
                      <TaskPill key={task.id} task={task} />
                    ))}
                    {dayTasks.length > 3 && (
                      <span className="text-[10px] text-navy/40 px-2">+{dayTasks.length - 3} more</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Week View */}
      {viewMode === 'week' && (
        <div className="bg-white rounded-xl border border-navy/10 overflow-hidden" data-testid="cal-week-view">
          <div className="grid grid-cols-7">
            {weekDays.map((date, i) => {
              const dayTasks = getTasksForDate(date);
              return (
                <div key={i} className={`border-r border-navy/5 last:border-r-0 ${isToday(date) ? 'bg-[#40CFDE]/5' : ''}`}>
                  {/* Day header */}
                  <div className="px-2 py-3 border-b border-navy/10 text-center bg-gray-50/50">
                    <p className="text-xs text-navy/50">{DAYS[i]}</p>
                    <p className={`text-lg font-bold mt-0.5 ${isToday(date) ? 'text-[#40CFDE]' : 'text-[#1A2B5E]'}`}>
                      {date.getDate()}
                    </p>
                    <p className="text-[10px] text-navy/40">{MONTHS[date.getMonth()].slice(0, 3)}</p>
                  </div>
                  {/* Tasks */}
                  <div className="p-2 space-y-1.5 min-h-[300px]">
                    {dayTasks.length === 0 ? (
                      <p className="text-xs text-navy/20 text-center pt-8">No tasks</p>
                    ) : dayTasks.map(task => {
                      const color = statusColors[task.status] || statusColors.todo;
                      const assignee = users.find(u => u.id === task.assignee_id);
                      return (
                        <button
                          key={task.id}
                          onClick={() => handleTaskClick(task)}
                          className={`w-full text-left p-2 rounded-lg border transition-all hover:shadow-sm ${task.is_occurrence ? 'border-dashed border-navy/30 bg-white' : 'border-navy/10'}`}
                          style={!task.is_occurrence ? { borderLeftWidth: '3px', borderLeftColor: color } : {}}
                          data-testid={`cal-week-task-${task.id}`}
                        >
                          <div className="flex items-center gap-1">
                            {(task.is_occurrence || task.recurrence) && <Repeat className="w-3 h-3 text-navy/40 flex-shrink-0" />}
                            <span className={`text-xs font-medium truncate ${task.is_occurrence ? 'text-navy/50 italic' : 'text-[#1A2B5E]'}`}>
                              {task.title}
                            </span>
                          </div>
                          {!task.is_occurrence && (
                            <div className="flex items-center gap-1 mt-1">
                              <Badge className={`text-[9px] px-1 py-0 ${priorityStyle(task.priority)}`}>{task.priority}</Badge>
                              {assignee && <span className="text-[9px] text-navy/40">{assignee.name?.split(' ')[0]}</span>}
                            </div>
                          )}
                          {task.is_occurrence && (
                            <p className="text-[9px] text-navy/40 mt-0.5 flex items-center gap-0.5">
                              <CheckSquare className="w-2.5 h-2.5" /> Click to claim
                            </p>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-6 text-xs text-navy/50">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-1 rounded bg-[#1A2B5E]" /> To Do
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-1 rounded bg-[#106EFD]" /> In Progress
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-1 rounded bg-[#E171EA]" /> In Review
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-1 rounded bg-[#37C563]" /> Complete
        </div>
        <div className="flex items-center gap-1.5">
          <Repeat className="w-3 h-3" /> Recurring (click to claim)
        </div>
      </div>

      {/* Task Edit Dialog */}
      <TaskEditDialog
        task={editingTask}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        users={users}
        projects={projects}
        onSaved={fetchData}
      />
    </div>
  );
};

export default CalendarPage;
